import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import type { Json } from "@/integrations/supabase/types";

export interface IngestionOutput {
  title?: string;
  summary?: string;
  description?: string;
  contentType?: string;
  tags?: Array<{ group: string; name: string }>;
  categories?: string[];
  tools?: string[];
  worksOfMercy?: string[];
  capabilities?: string[];
  causes?: string[];
  phase?: string;
  audienceType?: string;
  metaTitle?: string;
  metaDescription?: string;
  ogTitle?: string;
  ogDescription?: string;
  keywords?: string[];
  // Fields from refresh preview
  cause?: string;
  audience_type?: string;
  meta_title?: string;
  meta_description?: string;
  thumbnail_url?: string;
}

export interface PendingSubmission {
  id: string;
  text_input: string | null;
  files: Array<{ name: string; path: string }>;
  urls: string[];
  status: string;
  created_at: string;
  updated_at: string;
  ingestion_output: IngestionOutput | null;
  submission_type: 'new_resource' | 'outdated_report';
  linked_resource_id: string | null;
  share_source_files: boolean;
  organization_id: string | null;
}

function parseFiles(files: Json | null): Array<{ name: string; path: string }> {
  if (!files || !Array.isArray(files)) return [];
  return files.map(f => {
    if (typeof f === 'string') return { name: f, path: f };
    if (typeof f === 'object' && f !== null) {
      const obj = f as Record<string, unknown>;
      return {
        name: String(obj.name || obj.path || 'Unknown'),
        path: String(obj.path || obj.name || '')
      };
    }
    return { name: 'Unknown', path: '' };
  });
}

function parseUrls(urls: Json | null): string[] {
  if (!urls || !Array.isArray(urls)) return [];
  return urls.filter((u): u is string => typeof u === 'string');
}

function parseIngestionOutput(output: Json | null): IngestionOutput | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) return null;
  return output as unknown as IngestionOutput;
}

export function usePendingSubmissions() {
  const { organization, isLoading: orgLoading } = useOrganization();
  const [submissions, setSubmissions] = useState<PendingSubmission[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubmissions = useCallback(async () => {
    if (!organization?.id) {
      setSubmissions([]);
      setCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const { data, error: queryError } = await supabase
        .from('pending_resources')
        .select('*')
        .eq('organization_id', organization.id)
        .in('status', ['ready_for_review', 'refresh_preview_ready'])
        .order('created_at', { ascending: false });

      if (queryError) {
        console.warn('Error fetching pending submissions:', queryError.message);
        setError(queryError.message);
        setSubmissions([]);
        setCount(0);
      } else {
        const mapped: PendingSubmission[] = (data || []).map(item => ({
          id: item.id,
          text_input: item.text_input,
          files: parseFiles(item.files),
          urls: parseUrls(item.urls),
          status: item.status,
          created_at: item.created_at,
          updated_at: item.updated_at,
          ingestion_output: parseIngestionOutput(item.ingestion_output),
          submission_type: ((item as any).submission_type || 'new_resource') as 'new_resource' | 'outdated_report',
          linked_resource_id: (item as any).linked_resource_id || null,
          share_source_files: (item as any).share_source_files || false,
          organization_id: item.organization_id || null
        }));
        setSubmissions(mapped);
        setCount(mapped.length);
      }
    } catch (e) {
      console.warn('Pending submissions fetch error:', e);
      setError(e instanceof Error ? e.message : 'Unknown error');
      setSubmissions([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    // Wait for org context to load
    if (orgLoading) return;
    
    fetchSubmissions();

    // Subscribe to realtime updates for this org
    if (!organization?.id) return;

    const channel = supabase
      .channel('pending-resources-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pending_resources',
          filter: `organization_id=eq.${organization.id}`
        },
        () => {
          fetchSubmissions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgLoading, organization?.id, fetchSubmissions]);

  return {
    submissions,
    count,
    loading: loading || orgLoading,
    error,
    refresh: fetchSubmissions
  };
}

export function usePendingCount() {
  const { organization, isLoading: orgLoading } = useOrganization();
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!organization?.id) return;
    
    try {
      const { count: totalCount, error } = await supabase
        .from('pending_resources')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organization.id)
        .in('status', ['ready_for_review', 'refresh_preview_ready']);

      if (error) {
        console.warn('Pending count query failed:', error.message);
        return;
      }
      
      if (totalCount !== null) {
        setCount(totalCount);
      }
    } catch (e) {
      console.warn('Pending count error:', e);
    }
  }, [organization?.id]);

  useEffect(() => {
    // Wait for org context to load
    if (orgLoading || !organization?.id) return;

    fetchCount();

    // Subscribe to realtime updates for this org
    const channel = supabase
      .channel('pending-count-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pending_resources',
          filter: `organization_id=eq.${organization.id}`
        },
        () => {
          fetchCount();
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn('Pending count realtime subscription error');
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgLoading, organization?.id, fetchCount]);

  return { count, refresh: fetchCount };
}
